import { computed, reactive, ref } from 'vue';

import { usePaymentForm } from '@/components/payment/components/payment-method/components/solid/solid-payment-method/hooks/use-payment-form/use-payment-form';
import { getPriceAlpha3Formatted } from '@/helpers/number';
import { getPackagePrice } from '@/pages/payment/helpers/package/package';
import { PaymentStatePackage } from '@/pages/payment/store/payment-state';
import { useSplit } from '@/services/growth-book/hooks/use-split/use-split';
import { useSplitGeoPaymentPrioritization } from '@/services/growth-book/hooks/use-split-geo-payment-prioritization/use-split-geo-payment-prioritization';
import { SETTINGS_DEFAULT_CURRENCY } from '@/settings';
import { getState, getStore } from '@/store/store-helper';

type UseSelfArg = {
  pkg: PaymentStatePackage;
  isApplePayVisible: boolean;
  isGooglePayVisible: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useSelf(arg: UseSelfArg) {
  // Ref -----------------------------------------------------------------------
  const isCardFormVisible = ref(false);

  // Hook ----------------------------------------------------------------------
  const paymentForm = usePaymentForm();
  const splitPrimer = useSplit('primer');
  const splitGeoPayment = useSplitGeoPaymentPrioritization();

  // Computed ------------------------------------------------------------------

  const isFromLanding = computed((): boolean => {
    return getState().common.index.isFromLanding;
  });

  const price = computed<string>(() => {
    const pkgPrice = getPackagePrice(arg.pkg);

    return getPriceAlpha3Formatted(
      pkgPrice.totalAmount ?? 0n,
      pkgPrice.currency ?? SETTINGS_DEFAULT_CURRENCY,
    );
  });

  const priceOld = computed<string>(() => {
    return getPriceAlpha3Formatted(
      arg.pkg.price?.baseAmount ?? 0n,
      arg.pkg.price?.currency ?? SETTINGS_DEFAULT_CURRENCY,
    );
  });

  const isGooglePayAvailable = computed(() => {
    return getState().payment.paymentMethods.googlePay.isAvailable;
  });

  const isApplePayAvailable = computed(() => {
    return paymentForm.isApplePayLoaded && getState().payment.paymentMethods.applePay.isAvailable;
  });

  const isPaypalAvailable = computed<boolean>(() => {
    return getStore().state.payment.paymentMethods.solidPaypal.isAvailable;
  });

  const geoPaymentVariant = computed(() => splitGeoPayment.variant);

  const isApplePayEnabled = computed(() => {
    return arg.isApplePayVisible && !splitPrimer.isV2 && geoPaymentVariant.value !== 'disable-apay';
  });

  const isGooglePayEnabled = computed(() => {
    return (
      arg.isGooglePayVisible && !splitPrimer.isV2 && geoPaymentVariant.value !== 'disable-gpay'
    );
  });

  const isApplePayIconVisible = computed(() => {
    return isApplePayAvailable.value && isApplePayEnabled.value;
  });

  const isGooglePayIconVisible = computed(() => {
    return isGooglePayAvailable.value && isGooglePayEnabled.value;
  });

  const isAppleGooglePayIconsVisible = computed(() => {
    return isApplePayIconVisible.value || isGooglePayIconVisible.value;
  });

  // Method --------------------------------------------------------------------
  function setIsCardFormVisible(value: boolean) {
    isCardFormVisible.value = value;
  }

  function mount() {
    setIsCardFormVisible(true);
  }

  function init() {
    splitPrimer.init();
    splitGeoPayment.init();
  }

  return reactive({
    isCardFormVisible,
    splitPrimer,
    splitGeoPayment,
    isFromLanding,
    price,
    priceOld,
    isGooglePayAvailable,
    isApplePayAvailable,
    isPaypalAvailable,
    geoPaymentVariant,
    isApplePayEnabled,
    isGooglePayEnabled,
    isApplePayIconVisible,
    isGooglePayIconVisible,
    isAppleGooglePayIconsVisible,
    setIsCardFormVisible,
    mount,
    init,
  });
}
